import type {MockAIGeneration} from "../../types";

// Mock AI generation data for Procure-to-Pay diagram
const p2pAIGeneration: MockAIGeneration = {
  generation_id: "gen-p2p-001",
  generated_at: "2025-01-10T14:32:15Z",
  model_version: "Platformed AI v1.2",
  prompt:
    "Generate a procure-to-pay process diagram showing segregation of duties across different roles in the purchasing workflow",
  diagram_type: "Business Process Flow",
  source_evidence: [
    {
      source_id: "doc-pol-001",
      source_name: "Purchasing Policy Manual v2.3",
      source_type: "document",
      source_context:
        "Section 4.2: Purchase Requisitions must be approved by the employee's direct manager before being sent to Procurement. Section 5.1: Purchase Orders exceeding $50,000 require additional approval from the Purchasing Manager. Section 6.3: Three-way matching is required between Purchase Order, Goods Receipt, and Vendor Invoice before payment authorization.",
      relevance_explanation:
        "This document establishes the approval hierarchy and segregation of duties that form the basis of the P2P workflow",
      extractions: [
        {
          citation_number: 1,
          quote:
            "Purchase Requisitions must be approved by the employee's direct manager before being sent to Procurement.",
          page_reference: "Section 4.2",
        },
        {
          citation_number: 2,
          quote: "Purchase Orders exceeding $50,000 require additional approval from the Purchasing Manager.",
          page_reference: "Section 5.1",
        },
        {
          citation_number: 3,
          quote:
            "Three-way matching is required between Purchase Order, Goods Receipt, and Vendor Invoice before payment authorization.",
          page_reference: "Section 6.3",
        },
      ],
    },
    {
      source_id: "doc-proc-002",
      source_name: "Procurement Standard Operating Procedures",
      source_type: "document",
      source_context:
        "The Receiving Department must independently verify and record all goods received against the Purchase Order. Discrepancies must be reported to Procurement immediately. The Accounts Payable team maintains the vendor master file and processes invoices only after three-way match validation.",
      relevance_explanation:
        "Defines the receiving and invoice processing controls that ensure proper validation before payment",
      extractions: [
        {
          citation_number: 4,
          quote:
            "The Receiving Department must independently verify and record all goods received against the Purchase Order.",
          page_reference: "Section 3.1",
        },
        {
          citation_number: 5,
          quote:
            "The Accounts Payable team maintains the vendor master file and processes invoices only after three-way match validation.",
          page_reference: "Section 4.2",
        },
      ],
    },
    {
      source_id: "img-flow-001",
      source_name: "P2P Process Flow Diagram (Legacy)",
      source_type: "image",
      source_context:
        "Existing flowchart shows: Requisition → Manager Approval → PO Creation → Vendor Fulfillment → Receiving → Invoice Processing → Payment. Includes decision points for approval thresholds and matching validations.",
      relevance_explanation: "Provided the high-level structure and key decision points in the process",
      extractions: [
        {
          citation_number: 6,
          quote:
            "Requisition → Manager Approval → PO Creation → Vendor Fulfillment → Receiving → Invoice Processing → Payment",
          page_reference: "Main Diagram",
        },
      ],
    },
    {
      source_id: "quest-sox-001",
      source_name: "SOX Controls Questionnaire",
      source_type: "questionnaire",
      source_context:
        "Q: Does the organization enforce segregation of duties between requisitioning, purchasing, receiving, and payment functions? A: Yes, these functions are performed by separate departments with independent approval workflows. Q: Are purchase orders required for all vendor purchases? A: Yes, with defined approval limits based on dollar thresholds.",
      relevance_explanation:
        "Confirmed the segregation of duties controls and approval requirements implemented in the process",
      extractions: [
        {
          citation_number: 7,
          quote: "These functions are performed by separate departments with independent approval workflows.",
          page_reference: "Question 12",
        },
        {
          citation_number: 8,
          quote: "Purchase orders required with defined approval limits based on dollar thresholds.",
          page_reference: "Question 15",
        },
      ],
    },
  ],
  reasoning:
    "Based on the source documents, I identified 7 distinct roles in the P2P process [6][7]: Requestor, Approving Manager, Procurement Team, Receiving Department, Accounts Payable, Payment Approver, and Treasury/Banking. The workflow follows a clear segregation of duties principle:\n\n1. **Requisition Phase**: Employee initiates, manager approves based on budget authority [1]\n2. **Procurement Phase**: Separate procurement team handles vendor selection and PO creation, with purchasing manager approval for high-value items [2][8]\n3. **Receiving Phase**: Independent receiving department validates goods against PO [4]\n4. **Invoice Processing Phase**: AP team performs three-way matching before processing [3][5]\n5. **Payment Phase**: Payment approver reviews, treasury executes payment\n\nThis structure ensures no single person can both authorize and execute purchases [7], maintaining strong internal controls as required by SOX compliance.",
  confidence_notes: [
    "High confidence in overall workflow structure based on consistent documentation across sources",
    "Approval thresholds ($50K for PO approval) confirmed in multiple documents",
    "Three-way matching control is explicitly stated in policy manual",
    "Role separation aligns with standard SOX P2P control framework",
  ],
};

// Mock AI generation data for Order-to-Cash diagram
const o2cAIGeneration: MockAIGeneration = {
  generation_id: "gen-o2c-001",
  generated_at: "2025-01-10T15:18:42Z",
  model_version: "Platformed AI v1.2",
  prompt:
    "Generate an order-to-cash process diagram highlighting revenue recognition, credit management, and cash collection controls",
  diagram_type: "Business Process Flow",
  source_evidence: [
    {
      source_id: "doc-rev-001",
      source_name: "Revenue Recognition Policy",
      source_type: "document",
      source_context:
        "Revenue is recognized when control of goods transfers to the customer, typically upon shipment or delivery. Credit approval is required before order processing for customers exceeding their credit limit. The Billing Department generates invoices automatically upon shipment confirmation.",
      relevance_explanation: "Establishes the revenue recognition criteria and credit control requirements",
      extractions: [
        {
          citation_number: 9,
          quote:
            "Revenue is recognized when control of goods transfers to the customer, typically upon shipment or delivery.",
          page_reference: "Section 2.1",
        },
        {
          citation_number: 10,
          quote: "Credit approval is required before order processing for customers exceeding their credit limit.",
          page_reference: "Section 3.2",
        },
        {
          citation_number: 11,
          quote: "The Billing Department generates invoices automatically upon shipment confirmation.",
          page_reference: "Section 4.1",
        },
      ],
    },
    {
      source_id: "doc-ar-002",
      source_name: "Accounts Receivable Procedures",
      source_type: "document",
      source_context:
        "The AR team applies payments to customer accounts and reconciles daily bank deposits. Collections follow-up begins at 30 days past due with escalating procedures at 60 and 90 days. Write-off approvals require Controller sign-off for amounts over $10,000.",
      relevance_explanation: "Details the cash collection and AR management processes",
      extractions: [
        {
          citation_number: 12,
          quote: "The AR team applies payments to customer accounts and reconciles daily bank deposits.",
          page_reference: "Section 2.3",
        },
        {
          citation_number: 13,
          quote: "Collections follow-up begins at 30 days past due with escalating procedures at 60 and 90 days.",
          page_reference: "Section 3.1",
        },
      ],
    },
    {
      source_id: "quest-rev-001",
      source_name: "Revenue Controls Assessment",
      source_type: "questionnaire",
      source_context:
        "Q: Are credit limits established for all customers? A: Yes, reviewed annually by Credit Manager. Q: Is there segregation between order fulfillment and billing? A: Yes, warehouse handles fulfillment, billing department independently generates invoices based on shipment data.",
      relevance_explanation: "Confirms segregation of duties and credit management controls",
      extractions: [
        {
          citation_number: 14,
          quote: "Credit limits established for all customers, reviewed annually by Credit Manager.",
          page_reference: "Question 8",
        },
        {
          citation_number: 15,
          quote:
            "Warehouse handles fulfillment, billing department independently generates invoices based on shipment data.",
          page_reference: "Question 11",
        },
      ],
    },
  ],
  reasoning:
    "The O2C process involves 9 distinct roles ensuring proper controls over revenue recognition and cash collection:\n\n1. **Order Phase**: Sales rep takes order, credit manager approves if needed [10][14], order processing enters into system\n2. **Fulfillment Phase**: Warehouse picks/packs goods, shipping department delivers with proof of delivery [15]\n3. **Revenue Recognition**: Billing generates invoice upon shipment (transfer of control) [9][11], accounting posts revenue\n4. **Collection Phase**: Treasury receives payments, AR applies payments and manages collections [12][13]\n\nKey controls identified:\n- Credit approval before fulfillment prevents bad debt [10]\n- Independent billing based on shipment data ensures accurate invoicing [11][15]\n- Segregation between order entry, fulfillment, billing, and cash handling prevents fraud [15]\n- Revenue recognized only upon shipment aligns with GAAP/ASC 606 [9]",
  confidence_notes: [
    "Clear segregation of duties across order, fulfillment, billing, and collections",
    "Revenue recognition timing (at shipment) explicitly stated in policy",
    "Credit approval process confirmed in multiple sources",
    "Standard O2C workflow consistent with industry best practices",
  ],
};

// Mock AI generation data for IT Access Provisioning diagram
const itAccessAIGeneration: MockAIGeneration = {
  generation_id: "gen-it-001",
  generated_at: "2025-01-10T16:05:23Z",
  model_version: "Platformed AI v1.2",
  prompt: "Generate an IT access provisioning diagram demonstrating user access lifecycle management and ITGC controls",
  diagram_type: "IT Process Flow",
  source_evidence: [
    {
      source_id: "doc-iam-001",
      source_name: "Identity and Access Management Policy",
      source_type: "document",
      source_context:
        "All access requests must be initiated by the user's manager and approved by the System Owner. IT Security reviews access requests for segregation of duties conflicts before provisioning. Access is granted based on job role using predefined access templates. Quarterly access reviews are required for all privileged accounts.",
      relevance_explanation: "Defines the access request approval workflow and review requirements",
      extractions: [
        {
          citation_number: 16,
          quote: "All access requests must be initiated by the user's manager and approved by the System Owner.",
          page_reference: "Section 3.1",
        },
        {
          citation_number: 17,
          quote: "IT Security reviews access requests for segregation of duties conflicts before provisioning.",
          page_reference: "Section 3.2",
        },
        {
          citation_number: 18,
          quote: "Access is granted based on job role using predefined access templates.",
          page_reference: "Section 3.3",
        },
        {
          citation_number: 19,
          quote: "Quarterly access reviews are required for all privileged accounts.",
          page_reference: "Section 4.2",
        },
      ],
    },
    {
      source_id: "doc-term-002",
      source_name: "Employee Termination Procedures",
      source_type: "document",
      source_context:
        "HR notifies IT Operations within 2 hours of termination. IT Operations immediately disables all system access. IT Security performs verification sweep within 24 hours. Compliance maintains audit trail of all access terminations.",
      relevance_explanation: "Establishes the termination workflow and timelines for access revocation",
      extractions: [
        {
          citation_number: 20,
          quote: "HR notifies IT Operations within 2 hours of termination.",
          page_reference: "Section 2.1",
        },
        {
          citation_number: 21,
          quote: "IT Operations immediately disables all system access.",
          page_reference: "Section 2.2",
        },
      ],
    },
    {
      source_id: "quest-itgc-001",
      source_name: "ITGC Questionnaire",
      source_type: "questionnaire",
      source_context:
        "Q: Are access requests approved by both management and system owners? A: Yes, dual approval required. Q: Is there a formal process for reviewing and removing access? A: Yes, quarterly reviews for privileged access, immediate revocation upon termination. Q: Are segregation of duties conflicts identified before granting access? A: Yes, IT Security reviews all requests against SoD matrix.",
      relevance_explanation: "Confirms multi-level approval process and ongoing access governance controls",
      extractions: [
        {
          citation_number: 22,
          quote: "Dual approval required (management and system owners).",
          page_reference: "Question 5",
        },
        {
          citation_number: 23,
          quote: "IT Security reviews all requests against SoD matrix.",
          page_reference: "Question 9",
        },
      ],
    },
  ],
  reasoning:
    "The IT access lifecycle involves 7 key roles demonstrating strong ITGC controls:\n\n1. **Request Phase**: Requestor initiates → Manager approves based on business need → System Owner confirms technical appropriateness [16][22]\n2. **Security Review**: IT Security checks for SoD conflicts and validates against policy [17][23]\n3. **Provisioning**: IT Operations grants access using role-based templates [18]\n4. **Monitoring**: Compliance monitors for policy violations and runs periodic reviews [19]\n5. **Termination**: HR triggers termination [20] → IT Operations disables access [21] → IT Security verifies → Compliance audits\n\nThis multi-layered approach ensures:\n- Dual approval (management + technical) before access granted [22]\n- SoD conflicts prevented through security review [17][23]\n- Role-based access reduces errors and ensures consistency [18]\n- Immediate revocation upon termination minimizes risk [20][21]\n- Independent compliance oversight provides audit trail [19]",
  confidence_notes: [
    "Multi-level approval process (Manager → System Owner → IT Security) clearly documented",
    "Termination workflow with specific timelines (2 hours, 24 hours) well-defined",
    "SoD review before provisioning is explicit control",
    "Aligns with NIST, ISO 27001, and SOX ITGC requirements",
  ],
};

// Map of diagram IDs to their AI generation data
export const DIAGRAM_AI_GENERATIONS: Record<string, MockAIGeneration> = {
  "hardcoded-p2p-001": p2pAIGeneration,
  "hardcoded-o2c-001": o2cAIGeneration,
  "hardcoded-it-access-001": itAccessAIGeneration,
};
